import { Tooltip } from '@mui/material'
import { useState, useMemo, useEffect, useRef } from 'react'

export interface OptionValue {
  uid: string
  store_label: string
  value_index: number
  swatch_data?: { __typename: string; value?: string | null } | null
}

export interface ConfigurableOption {
  attribute_code: string
  label: string
  position: number
  values: OptionValue[]
}

export interface VariantAttribute {
  code: string
  uid: string
  value_index?: number
  label?: string
}

export interface VariantProduct {
  sku: string
  stock_status: string
}

export interface Variant {
  attributes: VariantAttribute[]
  product: VariantProduct
}

interface VariantSelectorProps {
  configurable_options: ConfigurableOption[]
  variants: Variant[]
  onSelectionChange: (selectedUids: string[] | null) => void
  externalSelection?: Record<string, string> | null
  onAttributeSelect?: (attributeCode: string, uid: string) => void
  compact?: boolean
}

export function VariantSelector({
  configurable_options,
  variants,
  onSelectionChange,
  externalSelection,
  onAttributeSelect,
  compact = false,
}: VariantSelectorProps) {
  const [selection, setSelection] = useState<Record<string, string>>({})
  const [selectionLabels, setSelectionLabels] = useState<Record<string, string>>({})
  const [expandedAttrs, setExpandedAttrs] = useState<Set<string>>(new Set())
  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange
  const initializedRef = useRef(false)

  const sortedOptions = useMemo(
    () => [...configurable_options].sort((a, b) => a.position - b.position),
    [configurable_options],
  )

  // Auto-select first available value for each attribute on mount
  useEffect(() => {
    if (initializedRef.current || sortedOptions.length === 0) return
    initializedRef.current = true
    const initial: Record<string, string> = {}
    const initialLabels: Record<string, string> = {}
    for (const option of sortedOptions) {
      const first = option.values[0]
      if (first) {
        initial[option.attribute_code] = first.uid
        if (first.store_label) initialLabels[option.attribute_code] = first.store_label
      }
    }
    if (Object.keys(initial).length === 0) return
    setSelection(initial)
    setSelectionLabels(initialLabels)
    const matchingVariant = variants.find((variant) =>
      Object.entries(initial).every(([code, uid]) =>
        variant.attributes.some((attr) => attr.code === code && attr.uid === uid),
      ),
    )
    onSelectionChangeRef.current(matchingVariant?.attributes.map((attr) => attr.uid) ?? null)
  }, [sortedOptions, variants])

  useEffect(() => {
    if (!externalSelection) return
    setSelection(externalSelection)
    const allSelected =
      Object.keys(externalSelection).length > 0 &&
      configurable_options.every((opt) => externalSelection[opt.attribute_code])
    if (!allSelected) {
      onSelectionChangeRef.current(null)
      return
    }
    const matchingVariant = variants.find((variant) =>
      Object.entries(externalSelection).every(([code, uid]) =>
        variant.attributes.some((attr) => attr.code === code && attr.uid === uid),
      ),
    )
    onSelectionChangeRef.current(matchingVariant?.attributes.map((attr) => attr.uid) ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSelection])

  const availableUids = useMemo(() => {
    if (!variants.length) return null
    const available = new Set<string>()
    for (const option of sortedOptions) {
      for (const value of option.values) {
        const testSelection = { ...selection, [option.attribute_code]: value.uid }
        const hasVariant = variants.some((variant) =>
          Object.entries(testSelection).every(([code, uid]) =>
            variant.attributes.some((attr) => attr.code === code && attr.uid === uid),
          ),
        )
        if (hasVariant) available.add(value.uid)
      }
    }
    return available
  }, [selection, sortedOptions, variants])

  const handleSelect = (attributeCode: string, uid: string, label: string) => {
    onAttributeSelect?.(attributeCode, uid)
    const newSelection = { ...selection, [attributeCode]: uid }
    setSelection(newSelection)
    setSelectionLabels((prev) => ({ ...prev, [attributeCode]: label }))

    const allSelected = sortedOptions.every((opt) => newSelection[opt.attribute_code])
    if (!allSelected) {
      onSelectionChange(null)
      return
    }

    const matchingVariant = variants.find((variant) =>
      Object.entries(newSelection).every(([code, selectedUid]) =>
        variant.attributes.some((attr) => attr.code === code && attr.uid === selectedUid),
      ),
    )

    onSelectionChange(matchingVariant?.attributes.map((attr) => attr.uid) ?? null)
  }

  if (!sortedOptions.length) return null

  return (
    <div className=''>
      {sortedOptions.map((option) => {
        const allValues = option.values.filter((v) => v?.uid)
        const filtered =
          availableUids === null ? allValues : allValues.filter((v) => availableUids.has(v.uid))
        const availableValues = filtered.length > 0 ? filtered : allValues

        if (!availableValues.length) return null

        const hasColorSwatches = availableValues.some(
          (v) => v?.swatch_data?.__typename === 'ColorSwatchData',
        )
        const selectedColorLabel = hasColorSwatches
          ? availableValues.find(
              (v) =>
                v?.uid === selection[option.attribute_code] &&
                (!selectionLabels[option.attribute_code] ||
                  selectionLabels[option.attribute_code] === v.store_label),
            )?.store_label
          : null

        const isThreeColTrigger =
          compact &&
          !hasColorSwatches &&
          (availableValues.length > 12 || option.label.toLowerCase().includes('size'))
        const isExpanded = expandedAttrs.has(option.attribute_code)

        return (
          <div key={option.attribute_code} className='mb-6'>
            <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
              {option.label}
              {selectedColorLabel && (
                <span className='font-medium text-black'>{` = ${selectedColorLabel.toUpperCase()}`}</span>
              )}
            </p>
            <div
              className={
                hasColorSwatches
                  ? 'grid grid-cols-4 gap-x-2 gap-y-5'
                  : !compact
                    ? 'grid grid-cols-1 gap-2'
                    : isThreeColTrigger
                      ? 'grid grid-cols-1 gap-2 lg:grid-cols-3'
                      : 'grid grid-cols-1 gap-2 lg:grid-cols-2'
              }
            >
              {availableValues.map((value, idx) => {
                const isSelected =
                  selection[option.attribute_code] === value.uid &&
                  (!selectionLabels[option.attribute_code] ||
                    selectionLabels[option.attribute_code] === value.store_label)
                const isColor = value.swatch_data?.__typename === 'ColorSwatchData'
                const hiddenOnMobile = isThreeColTrigger && !isExpanded && idx >= 8
                return (
                  <div key={value.uid} className={hiddenOnMobile ? 'hidden lg:block' : undefined}>
                    {isColor ? (
                      <Tooltip title={value.store_label} placement='top' arrow>
                        <div className='relative'>
                          <button
                            type='button'
                            aria-label={value.store_label}
                            onClick={() => handleSelect(option.attribute_code, value.uid, value.store_label)}
                            className={`h-9 w-full rounded-[6px] transition-all active:scale-[0.95] ${
                              isSelected
                                ? 'ring-2 ring-[#5D54DE] ring-offset-2'
                                : 'ring-1 ring-[#d9d9d9] hover:ring-[#afafaf]'
                            }`}
                            style={{ backgroundColor: value.swatch_data?.value ?? '#ccc' }}
                          />
                          {isSelected && (
                            <span className='absolute left-1/2 top-full -translate-x-1/2 pt-0.5 whitespace-nowrap text-center text-[9px] font-medium leading-tight text-black'>
                              {value.store_label}
                            </span>
                          )}
                        </div>
                      </Tooltip>
                    ) : (
                      <button
                        type='button'
                        onClick={() => handleSelect(option.attribute_code, value.uid, value.store_label)}
                        className={`w-full overflow-hidden rounded-[4px] px-2.5 py-2.5 transition-all active:scale-[0.97] ${
                          isSelected
                            ? 'bg-[#D1CBCB33] text-black border-[1.6px] border-[#5D54DE] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)]'
                            : 'bg-[#F8F8F8] text-black border-[1.4px] border-[#908B8B]'
                        }`}
                      >
                        <span
                          className={`block truncate text-[13px] uppercase tracking-[0.04em] ${isSelected ? 'font-bold' : 'font-medium'}`}
                        >
                          {value.store_label}
                        </span>
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
            {isThreeColTrigger && availableValues.length > 8 && (
              <button
                type='button'
                className='mt-2 text-[11px] font-medium text-[#5D54DE] lg:hidden'
                onClick={() =>
                  setExpandedAttrs((prev) => {
                    const next = new Set(prev)
                    if (isExpanded) next.delete(option.attribute_code)
                    else next.add(option.attribute_code)
                    return next
                  })
                }
              >
                {isExpanded ? 'Show fewer options' : `${availableValues.length - 8} more options`}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
